import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import TableViewRoundedIcon from "@mui/icons-material/TableViewRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import SchemaRoundedIcon from "@mui/icons-material/SchemaRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";

import { downloadAssetReport, getAssetReport, validateAssetReport } from "../../api";
import { logError, logInfo } from "../../logging/loggingService";

const fmtCurrency = (value) => {
    if (value === null || value === undefined) {
        return "—";
    }
    const number = Number(value);
    if (Number.isNaN(number)) {
        return "—";
    }
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(number);
};

const fmtDate = (value) => {
    if (!value) return "";
    try {
        return new Intl.DateTimeFormat("de-DE").format(new Date(value));
    } catch (e) {
        return value;
    }
};

export default function AssetReport() {
    const currentYear = new Date().getFullYear().toString();
    const [period, setPeriod] = useState(currentYear);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(null);
    const [validationResult, setValidationResult] = useState(null);
    const [validationLoading, setValidationLoading] = useState(false);
    const [validationError, setValidationError] = useState(null);

    const hasEntries = useMemo(() => Array.isArray(data?.entries) && data.entries.length > 0, [data]);

    const loadReport = useCallback(async () => {
        const trimmed = (period || "").trim();
        if (!/^\d{4}$/.test(trimmed)) {
            setError("Bitte geben Sie ein Jahr im Format JJJJ an.");
            setData(null);
            setValidationResult(null);
            return;
        }
        setLoading(true);
        setError(null);
        setValidationResult(null);
        setValidationError(null);
        try {
            const res = await getAssetReport(trimmed);
            setData(res?.data || null);
            logInfo("Asset report geladen", { component: "AssetReport", period: trimmed });
        } catch (err) {
            logError("Fehler beim Laden des Anlagenspiegels", err, { component: "AssetReport" });
            setError(err?.message || "Anlagenspiegel konnte nicht geladen werden.");
            setData(null);
            setValidationResult(null);
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        loadReport();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDownload = useCallback(async (format) => {
        const trimmed = (period || "").trim();
        if (!/^\d{4}$/.test(trimmed)) {
            setError("Bitte geben Sie ein Jahr im Format JJJJ an.");
            return;
        }
        setDownloading(format);
        try {
            const res = await downloadAssetReport(trimmed, format);
            const blob = res?.data;
            if (!blob) {
                throw new Error("Download fehlgeschlagen");
            }
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = res.filename || `Anlagenspiegel.${format}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            logError("Fehler beim Export des Anlagenspiegels", err, { component: "AssetReport", format });
            setError(err?.message || "Download fehlgeschlagen.");
        } finally {
            setDownloading(null);
        }
    }, [period]);

    const handleValidate = useCallback(async () => {
        const trimmed = (period || "").trim();
        if (!/^\d{4}$/.test(trimmed)) {
            setError("Bitte geben Sie ein Jahr im Format JJJJ an.");
            return;
        }
        setValidationLoading(true);
        setValidationError(null);
        try {
            const res = await validateAssetReport(trimmed);
            setValidationResult(res?.data || null);
            logInfo("Asset report Validierung abgeschlossen", { component: "AssetReport", period: trimmed });
        } catch (err) {
            logError("Fehler bei der Validierung des Anlagenspiegels", err, { component: "AssetReport" });
            setValidationError(err?.message || "Validierung fehlgeschlagen.");
            setValidationResult(null);
        } finally {
            setValidationLoading(false);
        }
    }, [period]);

    return (
        <Box sx={{ maxWidth: 1200, mx: "auto", mt: 4, px: { xs: 2, md: 0 }, pb: 6 }}>
            <Card sx={{ borderRadius: 3, boxShadow: "0 6px 24px rgba(0,0,0,0.06)" }}>
                <CardHeader
                    title="Anlagenspiegel"
                    subheader="Summen aus Anlagevermögen, Abgängen, AfA und Restwert je Jahr"
                />
                <Divider />
                <CardContent>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-end" }} sx={{ mb: 3 }}>
                        <TextField
                            label="Jahr"
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                            type="number"
                            inputProps={{ min: 2000, max: 9999 }}
                            sx={{ maxWidth: 180 }}
                        />
                        <Button
                            variant="contained"
                            onClick={loadReport}
                            disabled={loading}
                            sx={{
                                backgroundColor: "orange",
                                "&:hover": { backgroundColor: "#cc7a00" },
                                color: "#000",
                                fontWeight: 700,
                                borderRadius: 2,
                                px: 3,
                            }}
                        >
                            Bericht laden
                        </Button>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button
                                variant="outlined"
                                startIcon={<PictureAsPdfRoundedIcon />}
                                onClick={() => handleDownload("pdf")}
                                disabled={downloading !== null || loading}
                            >
                                PDF
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<TableViewRoundedIcon />}
                                onClick={() => handleDownload("excel")}
                                disabled={downloading !== null || loading}
                            >
                                Excel
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<FileDownloadRoundedIcon />}
                                onClick={() => handleDownload("csv")}
                                disabled={downloading !== null || loading}
                            >
                                DATEV-CSV
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<SchemaRoundedIcon />}
                                onClick={() => handleDownload("xbrl")}
                                disabled={downloading !== null || loading}
                            >
                                E-Bilanz (XBRL)
                            </Button>
                            <Button
                                variant="outlined"
                                color="success"
                                startIcon={<FactCheckRoundedIcon />}
                                onClick={handleValidate}
                                disabled={loading || validationLoading}
                            >
                                Prüfen
                            </Button>
                        </Stack>
                        {(downloading || validationLoading) && <CircularProgress size={24} />}
                    </Stack>

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {validationError && <Alert severity="error" sx={{ mb: 2 }}>{validationError}</Alert>}
                    {loading && (
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <CircularProgress size={24} />
                            <Typography color="text.secondary">Bericht wird geladen…</Typography>
                        </Stack>
                    )}

                    {validationResult && (
                        <Alert
                            severity={validationResult.hasUnmappedAccounts ? "warning" : "success"}
                            sx={{ mb: 2 }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Validierung {validationResult.hasUnmappedAccounts ? "mit Hinweisen" : "erfolgreich"}
                            </Typography>
                            <Typography variant="body2">
                                {validationResult.hasUnmappedAccounts
                                    ? `${validationResult.unmappedAccounts.length} von ${validationResult.totalEntries} Einträgen benötigen eine Kontenzuordnung.`
                                    : `Alle ${validationResult.totalEntries} Einträge verfügen über eine Kontenzuordnung.`}
                            </Typography>
                            {validationResult.hasUnmappedAccounts && Array.isArray(validationResult.unmappedAccounts) && (
                                <Box component="div" sx={{ mt: 1 }}>
                                    {validationResult.unmappedAccounts.map((entry) => (
                                        <Typography key={entry.assetId || entry.assetName} variant="body2">
                                            {entry.assetName || "Unbenannt"}
                                            {entry.accountNumber ? ` (Konto ${entry.accountNumber})` : " – kein Konto hinterlegt"}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </Alert>
                    )}

                    {data && (
                        <>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Jahr {data.year}
                            </Typography>
                            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3 }}>
                                <Table size="small" aria-label="Anlagenspiegel">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Anlagegut</TableCell>
                                            <TableCell>Anschaffung</TableCell>
                                            <TableCell align="right">Zugang</TableCell>
                                            <TableCell align="right">Abgang</TableCell>
                                            <TableCell align="right">AfA</TableCell>
                                            <TableCell align="right">Restwert</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {hasEntries ? (
                                            data.entries.map((entry) => (
                                                <TableRow key={entry.assetId || entry.assetName}>
                                                    <TableCell>{entry.assetName || "Unbenannt"}</TableCell>
                                                    <TableCell>{fmtDate(entry.acquisitionDate)}</TableCell>
                                                    <TableCell align="right">{fmtCurrency(entry.additions)}</TableCell>
                                                    <TableCell align="right">{fmtCurrency(entry.disposals)}</TableCell>
                                                    <TableCell align="right">{fmtCurrency(entry.depreciation)}</TableCell>
                                                    <TableCell align="right">{fmtCurrency(entry.closingBalance)}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                    <Typography color="text.secondary">Keine Daten für dieses Jahr.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {data.totals && (
                                            <TableRow sx={{ fontWeight: 700 }}>
                                                <TableCell sx={{ fontWeight: 700 }}>Summe</TableCell>
                                                <TableCell />
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtCurrency(data.totals.additions)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtCurrency(data.totals.disposals)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtCurrency(data.totals.depreciation)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtCurrency(data.totals.closingBalance)}</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

